/**
 * DatePicker — Popover + Calendar single-date picker
 *
 * Uses the existing Calendar component (react-day-picker) + Popover (Radix).
 * Displays a button with formatted date, opens a calendar on click.
 *
 * Props:
 *  - value: Date | string | null
 *  - onChange: (dateString: string | '') => void  — ISO date string (YYYY-MM-DD) or ''
 *  - placeholder: string
 *  - className: string — extra classes for the trigger button
 *  - disabled: boolean
 *  - disabledDays: react-day-picker matcher(s) for non-selectable days
 *      (e.g. [{ before: today }, { dayOfWeek: [0, 6] }])
 *  - startMonth: Date — earliest navigable month
 *  - allowClear: boolean — show the clear (X) affordance (default true)
 *  - align: PopoverContent align ('start' | 'center' | 'end')
 */

import * as React from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

 
export function DatePicker({
  value,
  onChange,
  placeholder = 'Selecione uma data',
  className,
  disabled = false,
  disabledDays,
  startMonth,
  allowClear = true,
  align = 'start',
}) {
  const [open, setOpen] = React.useState(false);

  // Normalise value to a Date object (or null)
  const dateObj = React.useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    if (typeof value === 'string' && value.length >= 10) {
      const parsed = parseISO(value);
      return isValid(parsed) ? parsed : null;
    }
    return null;
  }, [value]);

  const handleSelect = (day) => {
    if (!day) {
      onChange('');
    } else {
      // Return YYYY-MM-DD string to keep it timezone-safe
      onChange(format(day, 'yyyy-MM-dd'));
    }
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal gap-2 h-9 px-3',
            'border-border bg-surface text-foreground-secondary hover:text-foreground-strong hover:bg-surface-hover',
            !dateObj && 'text-foreground-muted',
            className,
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
          <span className="truncate">
            {dateObj
              ? format(dateObj, "dd 'de' MMM, yyyy", { locale: ptBR })
              : placeholder}
          </span>
          {allowClear && dateObj && (
            <X
              className="ml-auto h-3.5 w-3.5 shrink-0 text-foreground-muted hover:text-foreground cursor-pointer"
              onClick={handleClear}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 border-border bg-background" align={align} sideOffset={4}>
        <Calendar
          mode="single"
          selected={dateObj}
          onSelect={handleSelect}
          defaultMonth={dateObj || new Date()}
          disabled={disabledDays}
          startMonth={startMonth}
          locale={ptBR}
        />
      </PopoverContent>
    </Popover>
  );
}
